// Framework
import * as Api from 'api/login';
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';
import {
  createLoginErrorMessage,
  pushFormattedError
} from 'helpers/formValidationHelper/formValidationMessage';
import Raven from 'raven-js';
import { clearForm } from './form';
import { createSelector } from 'reselect';
import { getClientCode } from 'actions/ui/init/initSelectors';
import { getLocale } from 'actions/language/selectors';
import langOptions from 'conf/dashboardLanguages';
import { push } from 'react-router-redux';
import { checkOnboarding } from 'actions/onboarding/onboardingActions';
import { ERROR_ACCESS_DENIED, ERROR_BAD_INPUT } from 'lib/errorTypes';

// Selectors
export const getAuth = (state) => state.login.authToken;
export const getMenu = (state) => state.login.menu;
export const getPermissions = (state) => state.login.permissions;
export const getAvailableProducts = (state) => state.login.availableProducts;
export const getIsLoggedIn = (state) => state.login.isLoggedIn;
export const getLangOptions = (state) => state.login.langOptions;

export const getFirstName = (state) => state.login.firstName;
export const getLastName = (state) => state.login.lastName;
export const getEmail = (state) => state.login.email;
export const getClientCodeFromLogin = (state) => state.login.clientCode;

export const getSortedMenu = createSelector([getMenu], (menu) =>
  menu.sort(function(a, b) {
    return a.order - b.order;
  })
);

export const getFirstMenuLink = createSelector([getSortedMenu], (menu) =>
  menu.length ? menu[0].link : 'home'
);

export const getMenuLinks = createSelector([getMenu], (menu) =>
  menu.map((menuItem) => menuItem.link)
);

export const getHomeLink = createSelector(
  [getLocale, getClientCode, getFirstMenuLink],
  (lang, clientCode, link) => `/${lang}/${clientCode}/${link}`
);

export const hasAnalyticsPermission = createSelector(
  [getPermissions, getMenuLinks],
  (permissions, menuLinks) =>
    menuLinks.includes('platform-usage') &&
    permissions.includes(window.config.REACT_APP_ANALYTICS_PERMISSION)
);

export const hasSHAWAnalyticsPermission = createSelector(
  [getPermissions, getMenuLinks],
  (permissions, menuLinks) =>
    menuLinks.includes('shaw-statistics') &&
    permissions.includes(window.config.REACT_APP_SHAW_ANALYTICS_PERMISSION)
);

export const hasAccountsUsersAndGroupsPermission = createSelector(
  [getPermissions, getMenuLinks],
  (permissions, menuLinks) =>
    menuLinks.includes('accounts') &&
    permissions.includes(window.config.REACT_APP_ACCOUNTS_USERS_PERMISSION) &&
    permissions.includes(window.config.REACT_APP_ACCOUNTS_GROUPS_PERMISSION)
);

export const hasCallCenterPermission = createSelector(
  [getPermissions, getMenuLinks],
  (permissions, menuLinks) =>
    menuLinks.includes('data-reports') &&
    permissions.includes(window.config.REACT_APP_CALL_CENTER_UTILIZATION)
);

export const getFullName = createSelector(
  [getFirstName, getLastName],
  (firstName, lastName) => `${firstName} ${lastName}`
);

export const getLangCodes = createSelector([getLangOptions], (langOptions) =>
  langOptions.map(({ code }) => code)
);

// Actions
export const LOGIN = 'AUTH/LOGIN';
export const LOGIN_SUCCESS = 'AUTH/LOGIN_SUCCESS';
export const UPDATE_USER = 'AUTH/UPDATE_USER';
export const SET_USER = 'AUTH/SET_USER';
export const UPDATE_MENU = 'AUTH/UPDATE_MENU';
export const SET_MENU = 'UI/SET_MAIN_MENU';
export const LOGOUT = 'AUTH/LOGOUT';

/*** Action Creators ***/
export const login = (email, password) => ({
  type: LOGIN,
  email,
  password
});

export const loginSuccess = ({
  authToken,
  first_name: firstName,
  last_name: lastName,
  contract_number: contractNumber,
  email,
  permissions,
  menu,
  clientCode,
  user_group_id: groupId,
  products: availableProducts
}) => ({
  type: LOGIN_SUCCESS,
  authToken,
  firstName,
  lastName,
  contractNumber,
  email,
  permissions,
  menu,
  clientCode,
  groupId,
  availableProducts
});

export const updateUser = () => ({
  type: UPDATE_USER
});

export const setUser = ({
  first_name: firstName,
  last_name: lastName,
  email,
  permissions,
  user_group_id: groupId,
  products: availableProducts
}) => ({
  type: SET_USER,
  firstName,
  lastName,
  email,
  permissions,
  groupId,
  availableProducts
});

export const updateMenu = () => ({
  type: UPDATE_MENU
});

export const setMenu = (menu) => ({
  type: SET_MENU,
  menu
});

//TODO: CLEAR EVERYTHING WHILE LOGGING OUT, menu states etc
export const logout = () => ({
  type: LOGOUT
});

/*** Reducer ***/
export const DEFAULT_STATE = {
  isLoggedIn: false,
  authToken: '',
  firstName: '',
  lastName: '',
  contractNumber: '',
  email: '',
  menu: [],
  permissions: [],
  langOptions,
  clientCode: '',
  availableProducts: []
};

export default function loginReducer(
  state = DEFAULT_STATE,
  {
    authToken,
    firstName,
    lastName,
    contractNumber,
    email,
    menu,
    permissions,
    error,
    type,
    clientCode,
    groupId,
    availableProducts
  } = {}
) {
  switch (type) {
    case LOGIN_SUCCESS:
      return {
        ...state,
        authToken,
        firstName,
        lastName,
        contractNumber,
        email,
        error,
        menu,
        permissions,
        isLoggedIn: true,
        clientCode,
        groupId,
        availableProducts
      };
    case LOGOUT:
      return {
        ...DEFAULT_STATE
      };
    case SET_MENU:
      return {
        ...state,
        menu
      };
    case SET_USER:
      return {
        ...state,
        firstName,
        lastName,
        email,
        permissions,
        groupId,
        availableProducts
      };

    default:
      return state;
  }
}

/*** Sagas ***/
// Workers
function* loginWorker({ email, password }) {
  try {
    const locale = yield select(getLocale);
    const clientCode = yield select(getClientCode);
    const { token_type, access_token } = yield call(
      Api.login,
      locale,
      clientCode,
      email,
      password
    );
    const authToken = `${token_type} ${access_token}`;
    const { data: userInfo, permissions } = yield call(
      Api.me,
      locale,
      authToken
    );
    const { menu } = yield call(Api.getSideMenu, locale, authToken);
    yield put(
      loginSuccess({ authToken, ...userInfo, permissions, clientCode, menu })
    );
    yield put(push(`home`));

    yield put(checkOnboarding());
  } catch (err) {
    if (err.id !== ERROR_BAD_INPUT) {
      Raven.captureException(new Error('Login Error'), {
        extra: { email, err }
      });
    }

    //TODO: refactor to only 1
    yield pushFormattedError(err, createLoginErrorMessage);
  }
}

function* logoutWorker() {
  yield put(clearForm());
  const locale = yield select(getLocale);
  const clientCode = yield select(getClientCode);
  yield put(push(`/${locale}/${clientCode}/login`));
}

function* updateMenuWorker() {
  try {
    const locale = yield select(getLocale);
    const authToken = yield select(getAuth);

    if (!authToken) {
      // Avoid 4xx errors.
      return;
    }

    const { menu } = yield call(Api.getSideMenu, locale, authToken);
    yield put(setMenu(menu));
  } catch (err) {
    if (err.id == ERROR_ACCESS_DENIED) {
      //when auth token expired
      yield put(logout());
    } else {
      yield pushFormattedError(err);
    }
  }
}

/**
 * Updates the user info such as permissions, name, email, etc.
 * This helps maintain a consistent state with what an Admin may
 * change about a given user.
 */
function* updateUserWorker() {
  try {
    const locale = yield select(getLocale);
    const authToken = yield select(getAuth);

    if (!authToken) {
      // Avoid 4xx errors.
      return;
    }

    const { data: userInfo, permissions } = yield call(
      Api.me,
      locale,
      authToken
    );
    yield put(setUser({ ...userInfo, permissions }));
  } catch (err) {
    if (err.id == ERROR_ACCESS_DENIED) {
      //when auth token expired
      yield put(logout());
    } else {
      yield pushFormattedError(err);
    }
  }
}

// Watchers
export function* loginSaga() {
  yield all([
    takeEvery(LOGIN, loginWorker),
    takeEvery(LOGOUT, logoutWorker),
    takeLatest(UPDATE_MENU, updateMenuWorker),
    takeLatest(UPDATE_USER, updateUserWorker)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/auth/login.js